import axios, {
	type AxiosError,
	type AxiosInstance,
	type InternalAxiosRequestConfig,
} from "axios";

import { env } from "@/config/env";
import { useAuthStore } from "@/stores/auth.store";

export const api: AxiosInstance = axios.create({
	baseURL: env.apiUrl,
	timeout: 15_000,
	headers: {
		"Content-Type": "application/json",
	},
});

/**
 * Instance « nue » (sans intercepteurs) réservée à l'appel /auth/refresh, pour
 * éviter toute récursion : un 401 sur le refresh lui-même ne doit pas relancer
 * un refresh.
 */
const rawApi: AxiosInstance = axios.create({
	baseURL: env.apiUrl,
	timeout: 15_000,
	headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const token = useAuthStore.getState().accessToken;
	if (token) {
		config.headers.set("Authorization", `Bearer ${token}`);
	}
	return config;
});

// Routes publiques : un 401 ne doit jamais déclencher de refresh ni de logout.
const PUBLIC_PATTERNS = [
	"/auth/login",
	"/auth/forgot-password",
	"/auth/reset-password",
	"/auth/refresh",
];

const isPublicEndpoint = (url: string | undefined): boolean =>
	!!url && PUBLIC_PATTERNS.some((p) => url.includes(p));

/**
 * Single-flight : un seul appel /auth/refresh à la fois. La rotation côté API
 * invalide l'ancien refresh token, donc plusieurs appels concurrents
 * échoueraient — toutes les requêtes en 401 partagent la même promesse.
 */
let refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
	const { refreshToken, setTokens, logout } = useAuthStore.getState();
	if (!refreshToken) {
		logout();
		throw new Error("no-refresh-token");
	}
	try {
		const res = await rawApi.post("/auth/refresh", { refreshToken });
		// Réponse enveloppée { data: { accessToken, refreshToken }, timestamp }
		const payload = (res.data?.data ?? res.data) as {
			accessToken: string;
			refreshToken: string;
		};
		setTokens({
			accessToken: payload.accessToken,
			refreshToken: payload.refreshToken,
		});
		return payload.accessToken;
	} catch (e) {
		logout();
		throw e;
	}
}

function getRefresh(): Promise<string> {
	if (!refreshPromise) {
		refreshPromise = doRefresh().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
}

api.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const original = error.config as
			| (InternalAxiosRequestConfig & { _retry?: boolean })
			| undefined;

		if (
			error.response?.status === 401 &&
			original &&
			!original._retry &&
			!isPublicEndpoint(original.url)
		) {
			original._retry = true;
			try {
				await getRefresh();
				// Rejoue la requête : l'intercepteur de requête réapplique le nouveau token.
				return api(original);
			} catch {
				// logout() a déjà été appelé dans doRefresh().
				return Promise.reject(error);
			}
		}

		return Promise.reject(error);
	},
);

/** Toutes les réponses API sont wrappées { data, timestamp } par le TransformInterceptor. */
export interface ApiEnvelope<T> {
	data: T;
	timestamp: string;
}

export const unwrap = <T>(p: Promise<{ data: ApiEnvelope<T> }>): Promise<T> =>
	p.then((r) => r.data.data);
